/**
 * Check-In Export / Import
 *
 * Export: fetches all conversations + messages via existing service layer, serializes to JSON, triggers download.
 * Import: parses exported JSON and writes directly to Firestore (same pattern as voiceConversation.ts).
 */

import { collection, doc, addDoc, writeBatch, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { getConversations, getConversationMessages } from './conversations';

// ── Export ────────────────────────────────────────────────────────────────────

interface ExportMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string | null;
  source?: string;
  model?: string;
}

interface ExportConversation {
  title: string;
  createdAt: string | null;
  updatedAt: string | null;
  summary: string | null;
  messages: ExportMessage[];
}

interface ExportFile {
  version: '1';
  app: 'myjobodyssey';
  exportedAt: string;
  conversations: ExportConversation[];
}

export async function exportAllCheckIns(): Promise<void> {
  const conversations = await getConversations({ skipCache: true });

  const details = await Promise.all(
    conversations.map(c => getConversationMessages(c.id))
  );

  const exportData: ExportFile = {
    version: '1',
    app: 'myjobodyssey',
    exportedAt: new Date().toISOString(),
    conversations: conversations.map((conv, i) => ({
      title: conv.title,
      createdAt: conv.createdAt,
      updatedAt: conv.updatedAt,
      summary: conv.summary,
      messages: details[i].messages.map(msg => {
        const m: ExportMessage = {
          role: msg.role,
          content: msg.content,
          createdAt: msg.createdAt,
        };
        if (msg.source) m.source = msg.source;
        if (msg.model) m.model = msg.model;
        return m;
      }),
    })),
  };

  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const a = document.createElement('a');
  a.href = url;
  a.download = `myjobodyssey-checkins-${date}.json`;
  a.click();

  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ── Import ────────────────────────────────────────────────────────────────────

export async function importCheckIns(
  file: File,
  userId: string
): Promise<{ imported: number }> {
  const text = await file.text();
  let data: ExportFile;
  try {
    data = JSON.parse(text) as ExportFile;
  } catch {
    throw new Error('Invalid file — could not parse JSON.');
  }

  if (data.version !== '1' || data.app !== 'myjobodyssey') {
    throw new Error(
      'Unrecognized file format. Please use a file exported from My Job Odyssey.'
    );
  }

  if (!Array.isArray(data.conversations) || data.conversations.length === 0) {
    throw new Error('No conversations found in the export file.');
  }

  let imported = 0;

  for (const conv of data.conversations) {
    // Create conversation document
    const convRef = await addDoc(collection(db, 'conversations'), {
      userId,
      title: conv.title || 'Imported Check-In',
      createdAt: conv.createdAt
        ? Timestamp.fromDate(new Date(conv.createdAt))
        : serverTimestamp(),
      updatedAt: conv.updatedAt
        ? Timestamp.fromDate(new Date(conv.updatedAt))
        : serverTimestamp(),
      summary: conv.summary ?? null,
      messageCount: conv.messages?.length ?? 0,
      importedAt: serverTimestamp(),
    });

    // Batch-write messages
    if (Array.isArray(conv.messages) && conv.messages.length > 0) {
      const batch = writeBatch(db);
      const messagesCol = collection(db, 'conversations', convRef.id, 'messages');

      for (const msg of conv.messages) {
        const msgRef = doc(messagesCol);
        const msgData: Record<string, unknown> = {
          role: msg.role,
          content: msg.content,
          createdAt: msg.createdAt
            ? Timestamp.fromDate(new Date(msg.createdAt))
            : serverTimestamp(),
        };
        if (msg.source) msgData['source'] = msg.source;
        if (msg.model) msgData['model'] = msg.model;
        batch.set(msgRef, msgData);
      }

      await batch.commit();
    }

    imported++;
  }

  return { imported };
}
